import React from "react";
import {
    Box,
    FormControl,
    MenuItem,
    Pagination,
    Select,
    Typography,
} from "@mui/material";

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

// Specialized page size options for Bookings page (no 10 option)
export const BOOKINGS_PAGE_SIZE_OPTIONS = [25, 50, 75, 100];

/**
 * GlobalPagination Component
 * Consistent pagination control across all admin panel listing pages.
 *
 * @param {number} page - Current active page (1-indexed)
 * @param {number} pageSize - Number of records displayed per page (10, 25, 50, 75, 100)
 * @param {number} totalRecords - Total number of records (optional if totalPages provided)
 * @param {number} totalPages - Explicit total pages from backend (optional)
 * @param {function} onPageChange - Callback when page changes: (newPage: number) => void
 * @param {function} onPageSizeChange - Callback when rows per page changes: (newSize: number) => void
 * @param {boolean} loading - Loading state indicator
 * @param {boolean} disabled - Whether pagination controls are disabled
 * @param {object} sx - Style overrides for the root container
 * @param {Array<number>} pageSizeOptions - Custom page size options (optional, defaults to PAGE_SIZE_OPTIONS)
 */
const GlobalPagination = ({
    page = 1,
    pageSize = 10,
    totalRecords,
    totalPages: customTotalPages,
    onPageChange,
    onPageSizeChange,
    loading = false,
    disabled = false,
    sx = {},
    pageSizeOptions = PAGE_SIZE_OPTIONS,
}) => {
    const numericPage = Math.max(1, Number(page) || 1);
    const numericPageSize = Number(pageSize) || 10;
    const hasTotalRecords = totalRecords !== undefined && totalRecords !== null && !isNaN(Number(totalRecords));
    const numericTotalRecords = hasTotalRecords ? Math.max(0, Number(totalRecords)) : null;

    // Calculate total pages
    const computedTotalPages = numericTotalRecords !== null
        ? Math.max(1, Math.ceil(numericTotalRecords / numericPageSize))
        : 1;
    const finalTotalPages = customTotalPages !== undefined && customTotalPages !== null && !isNaN(Number(customTotalPages))
        ? Math.max(1, Number(customTotalPages))
        : computedTotalPages;

    // Calculate record range
    let rangeText = "";
    if (numericTotalRecords !== null) {
        if (numericTotalRecords === 0) {
            rangeText = "0–0 of 0";
        } else {
            const startRecord = (numericPage - 1) * numericPageSize + 1;
            const endRecord = Math.min(numericPage * numericPageSize, numericTotalRecords);
            rangeText = `${startRecord}–${endRecord} of ${numericTotalRecords}`;
        }
    } else {
        rangeText = `Page ${numericPage} of ${finalTotalPages}`;
    }

    const handlePageChange = (_, newPage) => {
        if (!loading && !disabled && newPage !== numericPage) {
            onPageChange?.(newPage);
        }
    };

    const handlePageSizeChange = (event) => {
        const newPageSize = Number(event.target.value);
        if (!loading && !disabled && newPageSize !== numericPageSize) {
            onPageSizeChange?.(newPageSize);
        }
    };

    const isInteractionDisabled = loading || disabled;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                py: 2,
                px: 2,
                mt: 2,
                bgcolor: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
                boxShadow: "none",
                ...sx,
            }}
        >
            {/* Rows per page selector */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: "0.8rem",
                        color: "#64748B",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                    }}
                >
                    Rows per page:
                </Typography>

                <FormControl size="small" sx={{ minWidth: 76 }}>
                    <Select
                        value={pageSizeOptions.includes(numericPageSize) ? numericPageSize : ''}
                        onChange={handlePageSizeChange}
                        disabled={isInteractionDisabled}
                        displayEmpty
                        renderValue={(selected) => selected || numericPageSize}
                        inputProps={{ "aria-label": "Rows per page" }}
                        sx={{
                            height: 32,
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            color: "#1E293B",
                            bgcolor: "#FFFFFF",
                            borderRadius: "6px",
                            boxShadow: "none",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E2E8F0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#CBD5E1",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#6E8964",
                                borderWidth: "1px",
                            },
                            "& .MuiSelect-select": {
                                py: 0.5,
                                px: 1.25,
                            },
                        }}
                    >
                        {pageSizeOptions.map((size) => (
                            <MenuItem
                                key={size}
                                value={size}
                                sx={{
                                    fontSize: "0.8rem",
                                    fontWeight: 400,
                                    "&.Mui-selected": {
                                        bgcolor: "#F1F5F9",
                                        fontWeight: 500,
                                    },
                                    "&:hover": {
                                        bgcolor: "#F8FAFC",
                                    },
                                }}
                            >
                                {size}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Record range */}
            <Typography
                variant="body2"
                sx={{
                    fontSize: "0.8rem",
                    color: "#64748B",
                    fontWeight: 500,
                }}
            >
                {rangeText}
            </Typography>

            {/* Page navigation buttons */}
            <Pagination
                count={finalTotalPages}
                page={Math.min(numericPage, finalTotalPages)}
                onChange={handlePageChange}
                disabled={isInteractionDisabled || finalTotalPages <= 1}
                size="small"
                shape="rounded"
                sx={{
                    "& .MuiPaginationItem-root": {
                        fontSize: "0.78rem",
                        color: "#475569",
                        border: "1px solid #E2E8F0",
                        borderRadius: "6px",
                        height: "28px",
                        minWidth: "28px",
                        margin: "0 2px",
                        boxShadow: "none",
                        "&:hover": {
                            bgcolor: "#F8FAFC",
                        },
                        "&.Mui-selected": {
                            bgcolor: "#6E8964",
                            color: "#FFFFFF",
                            borderColor: "#6E8964",
                            fontWeight: 500,
                            "&:hover": {
                                bgcolor: "#5d7555",
                            },
                        },
                        "&.Mui-disabled": {
                            opacity: 0.45,
                            borderColor: "#E2E8F0",
                        },
                    },
                }}
            />
        </Box>
    );
};

export default GlobalPagination;